/**
 * HR Remote 頁面配置
 *
 * 定義所有 hr 模組的頁面，自動生成：
 * - vite.config exposes
 * - remoteModules
 * - pagePermissions
 * - sideNavMenus
 */

import type { RemoteModuleConfig, PageConfig } from './types';

// ============================================================================
// 頁面配置
// ============================================================================

export const pages: PageConfig[] = [
    // ===== 摘要 =====
    {
        name: 'Dashboard',
        path: './views/overview/Dashboard.vue',
        title: '人資首頁',
        mode: 'hr',
        icon: 'icofont-dashboard-web',
        default: true,
        showInSideNav: true,
        standalone: { show: true, group: '摘要', order: 1 },
    },
    {
        name: 'OnDuty',
        path: './views/overview/OnDuty.vue',
        title: '今日出勤',
        mode: 'hr',
        default: true,
        standalone: { show: true, group: '摘要', order: 2 },
    },
    {
        name: 'MySchedule',
        path: './views/overview/MySchedule.vue',
        title: '我的班表',
        mode: 'hr',
        icon: 'icofont-ui-calendar',
        default: true,
        showInSideNav: true,
        standalone: { show: true, group: '摘要', order: 3 },
    },
    {
        name: 'MySpecialLeave',
        path: './views/overview/MySpecialLeave.vue',
        title: '我的特休',
        mode: 'hr',
        default: true,
        standalone: { show: true, group: '摘要', order: 4 },
    },
    {
        name: 'MyDepartment',
        path: './views/overview/MyDepartment.vue',
        title: '我的部門',
        mode: 'hr',
        default: true,
        standalone: { show: true, group: '摘要', order: 5 },
    },

    // ===== 表單簽核 =====
    {
        name: 'FormApplication',
        path: './views/form/FormApplication.vue',
        title: '申請表單',
        mode: 'hr',
        icon: 'icofont-ui-file',
        default: true,
        showInSideNav: true,
        standalone: { show: true, group: '表單簽核', order: 1 },
    },
    {
        name: 'NeedSignOff',
        path: './views/form/NeedSignOff.vue',
        title: '待簽核',
        mode: 'hr',
        icon: 'icofont-pencil-alt-2',
        default: true,
        showInSideNav: true,
        standalone: { show: true, group: '表單簽核', order: 2 },
    },
    {
        name: 'UnderApproval',
        path: './views/form/UnderApproval.vue',
        title: '審核中',
        mode: 'hr',
        default: true,
        standalone: { show: true, group: '表單簽核', order: 3 },
    },
    {
        name: 'AlreadySigned',
        path: './views/form/AlreadySigned.vue',
        title: '已簽核',
        mode: 'hr',
        default: true,
        standalone: { show: true, group: '表單簽核', order: 4 },
    },
    {
        name: 'AlreadyRejected',
        path: './views/form/AlreadyRejected.vue',
        title: '已駁回',
        mode: 'hr',
        default: true,
        standalone: { show: true, group: '表單簽核', order: 5 },
    },
    {
        name: 'ProcessManagement',
        path: './views/form/ProcessManagement.vue',
        title: '流程管理',
        mode: 'hr',
        default: false,
        standalone: { show: true, group: '表單簽核', order: 6 },
    },
    {
        name: 'RecordManagement',
        path: './views/form/RecordManagement.vue',
        title: '紀錄管理',
        mode: 'hr',
        default: false,
        standalone: { show: true, group: '表單簽核', order: 7 },
    },

    // ===== 管理 =====
    {
        name: 'StaffList',
        path: './views/manage/StaffList.vue',
        title: '員工名冊',
        mode: 'hr',
        icon: 'icofont-users',
        default: false,
        showInSideNav: true,
        standalone: { show: true, group: '管理', order: 1 },
    },
    {
        name: 'EmployeeStatus',
        path: './views/manage/EmployeeStatus.vue',
        title: '員工狀態',
        mode: 'hr',
        default: false,
        standalone: { show: true, group: '管理', order: 2 },
    },
    {
        name: 'AttendanceStatus',
        path: './views/manage/AttendanceStatus.vue',
        title: '出勤管理',
        mode: 'hr',
        default: false,
        standalone: { show: true, group: '管理', order: 3 },
    },
    {
        name: 'LeaveStatus',
        path: './views/manage/LeaveStatus.vue',
        title: '請假管理',
        mode: 'hr',
        default: false,
        standalone: { show: true, group: '管理', order: 4 },
    },
    {
        name: 'OvertimeStatus',
        path: './views/manage/OvertimeStatus.vue',
        title: '加班管理',
        mode: 'hr',
        default: false,
        standalone: { show: true, group: '管理', order: 5 },
    },
    {
        name: 'BusinessTripStatus',
        path: './views/manage/BusinessTripStatus.vue',
        title: '出差管理',
        mode: 'hr',
        default: false,
        standalone: { show: true, group: '管理', order: 6 },
    },
    {
        name: 'SpecialLeaveHours',
        path: './views/manage/SpecialLeaveHours.vue',
        title: '特休時數',
        mode: 'hr',
        default: false,
        standalone: { show: true, group: '管理', order: 7 },
    },
    {
        name: 'WorkScheduling',
        path: './views/manage/WorkScheduling.vue',
        title: '排班管理',
        mode: 'hr',
        default: false,
        standalone: { show: true, group: '管理', order: 8 },
    },
    {
        name: 'TimeManagement',
        path: './views/manage/TimeManagement.vue',
        title: '時間管理',
        mode: 'hr',
        default: false,
        standalone: { show: true, group: '管理', order: 9 },
    },
    {
        name: 'JobAgentSettings',
        path: './views/manage/JobAgentSettings.vue',
        title: '職務代理',
        mode: 'hr',
        default: false,
        standalone: { show: true, group: '管理', order: 10 },
    },
    {
        name: 'VehicleLoan',
        path: './views/manage/VehicleLoan.vue',
        title: '車輛借用',
        mode: 'hr',
        default: false,
        standalone: { show: true, group: '管理', order: 11 },
    },

    // ===== 報表 =====
    {
        name: 'Summary',
        path: './views/report/Summary.vue',
        title: '統計報表',
        mode: 'hr',
        default: false,
        standalone: { show: true, group: '報表', order: 1 },
    },
    {
        name: 'PersonnelMovement',
        path: './views/report/PersonnelMovement.vue',
        title: '人事異動',
        mode: 'hr',
        default: false,
        standalone: { show: true, group: '報表', order: 2 },
    },

    // ===== 設定 =====
    {
        name: 'Department',
        path: './views/setup/Department.vue',
        title: '部門設定',
        mode: 'hr',
        icon: 'icofont-company',
        default: false,
        showInSideNav: true,
        standalone: { show: true, group: '設定', order: 1 },
    },
    {
        name: 'AddNewEmployees',
        path: './views/setup/AddNewEmployees.vue',
        title: '新增員工',
        mode: 'hr',
        default: false,
        standalone: { show: true, group: '設定', order: 2 },
    },
    {
        name: 'HolidayWorkingDays',
        path: './views/setup/HolidayWorkingDays.vue',
        title: '假日設定',
        mode: 'hr',
        default: false,
        standalone: { show: true, group: '設定', order: 3 },
    },
];

// ============================================================================
// 模組完整配置
// ============================================================================

export const config: RemoteModuleConfig = {
    name: 'hr',
    federationName: 'hr',
    port: 7003,
    pages,
};

export default config;
